import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import cx from 'classnames';

import RhythmStylePicker from '../common/RhythmStylePicker';

import { ReactComponent as CalloutIcon } from '../../icons/callout.svg';
import { ReactComponent as CountInIcon } from '../../icons/count-in.svg';
import { ReactComponent as IntensityIcon } from '../../icons/intensity.svg';
import { ReactComponent as LoopIcon } from '../../icons/loop.svg';
import { ReactComponent as LyricsIcon } from '../../icons/microphone.svg';
import { ReactComponent as PlayThroughIcon } from '../../icons/play-through.svg';
import { ReactComponent as RepeatsIcon } from '../../icons/repeat-times.svg';
import { ReactComponent as AuditionIcon } from '../../icons/ear.svg';
import { ReactComponent as VoicingIcon } from '../../icons/voicing.svg';

import './ChordBarsTransport.scss';

/** An icon that switches something on and off. The label is its name, and leads the tooltip. */
const Toggle = ({ label, hint, icon, on, disabled = false, onToggle }) => (
  <button
    type="button"
    className={cx('toggle', { on })}
    title={hint ? `${label} — ${hint}` : label}
    aria-label={label}
    aria-pressed={!!on}
    disabled={disabled}
    onClick={onToggle}
  >
    {icon}
  </button>
);

Toggle.propTypes = {
  label: PropTypes.string.isRequired,
  hint: PropTypes.string,
  icon: PropTypes.node.isRequired,
  on: PropTypes.bool,
  disabled: PropTypes.bool,
  onToggle: PropTypes.func.isRequired,
};

/**
 * The controls a preview is played from, shared by the chart and MIDI modals so they look and
 * sound alike. Holds no state of its own. Play is not here: every dialog carries it in its header,
 * where a body taller than the screen can't push it out of reach — and so are the levels, in the
 * ChordBarsMixer sheet a header button drops down.
 *
 * Laid out by what each control is: the switches first, then the picker rows under them — the
 * instrument a caller passes in, then Rhythm.
 *
 * @param {object} player - From useChordBarsPreview or useMidiPreview. Loop and Voicing show
 *   only when the player has them.
 * @param {node} children - Rendered under the switches and above Rhythm, e.g. an instrument row
 * @param {{on: boolean, onToggle: function}} [audition] - The builder's Audition toggle
 * @param {{choice: string, onChoose: function, timeSignature: string}} [rhythm] - Adds the Rhythm row
 *   and its sheet; see RhythmStylePicker
 * @param {string} [rhythmDefault] - The style marked Default in that sheet
 * @param {{on: boolean, onToggle: function, title: string, disabled: boolean}} [lyrics] - Follow the
 *   chart through the part's words, where it has some
 * @param {{on: boolean, onToggle: function, disabled: boolean}} [repeats] - Play each part as many
 *   times as it repeats, where one does
 * @param {{on: boolean, onToggle: function}} [playThrough] - Carry on from this part to the end of the
 *   song, where more parts follow it
 * @param {{on: boolean, onToggle: function, disabled: boolean}} [intensity] - Play each part at the
 *   intensity it is arranged at; off, as if none had been set
 * @param {{on: boolean, onToggle: function, disabled: boolean}} [callouts] - Say each part's name a
 *   bar before it comes in, while playing through
 */
const ChordBarsTransport = ({
  player,
  audition,
  rhythm,
  rhythmDefault,
  lyrics,
  repeats,
  playThrough,
  intensity,
  callouts,
  children,
}) => {
  const { t } = useTranslation();
  // What changes the notes scheduled can't change under them, so it's greyed until Stop — and so is
  // Count-in, which has nothing left to do once playing has started
  const isBusy = Boolean(player.isPlaying || player.isLoading);

  return (
    <div className="ChordBarsTransport">
      {/* Left-aligned squares, the shape of the lyrics editor's actions, and scrolled sideways the
          way that row is when they run past a phone's width */}
      <div className="toggles horizontal-menu">
        <Toggle
          label={t('player.countIn')}
          icon={<CountInIcon />}
          on={player.countIn}
          disabled={isBusy}
          onToggle={() => player.setCountIn((on) => !on)}
        />
        {/* Greyed while what plays has an end of its own — parts one after another, or repeats */}
        {player.setLoop && (
          <Toggle
            label={t('player.loop')}
            icon={<LoopIcon />}
            on={player.loop}
            disabled={player.canLoop === false || isBusy}
            onToggle={() => player.setLoop((on) => !on)}
          />
        )}
        {/* Beside Loop, which it greys out */}
        {repeats && (
          <Toggle
            label={t('player.repeats')}
            hint={t('player.repeatsHint')}
            icon={<RepeatsIcon />}
            on={repeats.on}
            disabled={repeats.disabled || isBusy}
            onToggle={repeats.onToggle}
          />
        )}
        {/* Not tied to Rhythm: block chords leap between roots most audibly. Slash chords keep their
          written inversion either way. */}
        {player.toggleVoicing && (
          <Toggle
            label={t('player.voicing')}
            hint={t('player.voicingHint')}
            icon={<VoicingIcon />}
            on={player.voicing}
            disabled={isBusy}
            onToggle={player.toggleVoicing}
          />
        )}
        {audition && (
          <Toggle
            label={t('player.audition')}
            hint={t('player.auditionHint')}
            icon={<AuditionIcon />}
            on={audition.on}
            onToggle={audition.onToggle}
          />
        )}
        {/* Beside Lyrics: playing through shows the whole run as words, and greys Lyrics */}
        {playThrough && (
          <Toggle
            label={t('player.playThrough')}
            hint={t('player.playThroughHint')}
            icon={<PlayThroughIcon />}
            on={playThrough.on}
            disabled={isBusy}
            onToggle={playThrough.onToggle}
          />
        )}
        {/* Beside Play through, the only time intensity plays */}
        {intensity && (
          <Toggle
            label={t('player.intensity')}
            hint={t('player.intensityHint')}
            icon={<IntensityIcon />}
            on={intensity.on}
            disabled={intensity.disabled || isBusy}
            onToggle={intensity.onToggle}
          />
        )}
        {/* Beside Play through, the only time there are parts to call. Switched live, so not greyed
            while playing. */}
        {callouts && (
          <Toggle
            label={t('player.callParts')}
            hint={t('player.callPartsHint')}
            icon={<CalloutIcon />}
            on={callouts.on}
            disabled={callouts.disabled}
            onToggle={callouts.onToggle}
          />
        )}
        {lyrics && (
          <Toggle
            label={t('player.lyrics')}
            hint={lyrics.title}
            icon={<LyricsIcon />}
            on={lyrics.on}
            disabled={lyrics.disabled}
            onToggle={lyrics.onToggle}
          />
        )}
      </div>

      {children}
      {/* Under the instrument, so the two picker rows read as one pair */}
      {rhythm && <RhythmStylePicker rhythm={rhythm} defaultChoice={rhythmDefault} player={player} disabled={isBusy} />}

      {player.error && <p className="playback-error">{player.error}</p>}
    </div>
  );
};

ChordBarsTransport.propTypes = {
  player: PropTypes.shape({
    error: PropTypes.string,
    isPlaying: PropTypes.bool,
    isLoading: PropTypes.bool,
    countIn: PropTypes.bool,
    setCountIn: PropTypes.func,
    loop: PropTypes.bool,
    setLoop: PropTypes.func,
    canLoop: PropTypes.bool,
    voicing: PropTypes.bool,
    toggleVoicing: PropTypes.func,
  }).isRequired,
  audition: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
  }),
  rhythm: PropTypes.shape({
    choice: PropTypes.string,
    onChoose: PropTypes.func,
    timeSignature: PropTypes.string,
  }),
  rhythmDefault: PropTypes.string,
  lyrics: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
    title: PropTypes.string,
    disabled: PropTypes.bool,
  }),
  repeats: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  playThrough: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
  }),
  intensity: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  callouts: PropTypes.shape({
    on: PropTypes.bool,
    onToggle: PropTypes.func,
    disabled: PropTypes.bool,
  }),
  children: PropTypes.node,
};

export default ChordBarsTransport;
